import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Youtube } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PDFViewer from './PDFViewer';
import ShareButton from './ShareButton';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentoViewPage = () => {
  const { slug } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewCount, setViewCount] = useState(null);
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const { userType } = useAuth();

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/documentos/${slug}`);
        setDocument(response.data);
        setError(null);
        
        // Track view for non-admin users
        if (userType !== 'admin') {
          try {
            await axios.post(`${BACKEND_URL}/api/documentos/${slug}/view`);
          } catch (viewErr) {
            console.log('View tracking failed (non-critical):', viewErr);
          }
        }
        
        // Fetch view count for admin users
        if (userType === 'admin') {
          try {
            const authToken = localStorage.getItem('authToken');
            const adminResponse = await axios.get(`${BACKEND_URL}/api/admin/documentos`, {
              headers: { Authorization: `Bearer ${authToken}` }
            });
            const doc = adminResponse.data.documents.find(d => d.slug === slug);
            if (doc) {
              setViewCount(doc.view_count);
            }
          } catch (adminErr) {
            console.log('Failed to fetch view count:', adminErr);
          }
        }
      } catch (err) {
        console.error('Error fetching document:', err);
        if (err.response?.status === 404) {
          setError('Documento no encontrado');
        } else {
          setError('Error al cargar el documento');
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchDocument();
    }
  }, [slug, userType]);

  const handleBackClick = () => {
    navigate('/documentos');
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'
    }`}>
      {/* Header */}
      <header className={`${
        isDarkMode ? 'bg-purple-800' : 'bg-purple-600'
      } text-white py-6 transition-colors duration-300`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBackClick}
              className={`p-2 rounded-full transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-purple-700 hover:bg-purple-600' 
                  : 'bg-purple-700 hover:bg-purple-800'
              }`}
              aria-label="Volver a documentos"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-2xl md:text-3xl font-bold">
              {loading ? 'Cargando...' : document?.title || 'Documento'}
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto ${
                isDarkMode ? 'border-purple-400' : 'border-purple-600'
              }`}></div>
              <p className="mt-4">Cargando documento...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className={`rounded-lg p-6 text-center ${
              isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'
            }`}>
              <p className="text-lg font-semibold">{error}</p>
              <button
                onClick={handleBackClick}
                className={`mt-4 px-6 py-2 rounded-lg font-semibold transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                Volver a documentos
              </button>
            </div>
          )}

          {/* Document Content */}
          {!loading && !error && document && (
            <div className={`rounded-lg p-8 ${
              isDarkMode ? 'bg-gray-800' : 'bg-white shadow-lg'
            }`}>
              {/* Document Summary with Share Button */}
              {document.summary && (
                <div className={`mb-6 p-4 rounded-lg border-l-4 ${
                  isDarkMode 
                    ? 'bg-purple-900/20 border-purple-500 text-purple-300' 
                    : 'bg-purple-50 border-purple-500 text-purple-700'
                }`}>
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                    <p className="italic flex-1">{document.summary}</p>
                    <ShareButton 
                      title={document.title} 
                      url={window.location.href}
                      isDarkMode={isDarkMode}
                    />
                  </div>
                </div>
              )}

              {/* View Count (Admin Only) */}
              {userType === 'admin' && viewCount !== null && (
                <div className={`mb-6 flex items-center space-x-2 text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  <Eye size={18} />
                  <span className="font-semibold">{viewCount} {viewCount === 1 ? 'vista' : 'vistas'}</span>
                </div>
              )}

              {/* Image (if exists) */}
              {document.image_url && (
                <div className="mb-6">
                  <img 
                    src={document.image_url} 
                    alt={document.title}
                    className="w-full rounded-lg shadow-md"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      console.error('Failed to load image');
                    }}
                  />
                </div>
              )}

              {/* Video Button (if exists) */}
              {document.video_url && (
                <div className="mb-6">
                  <a
                    href={document.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                      isDarkMode 
                        ? 'bg-red-700 hover:bg-red-600 text-white' 
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    <Youtube size={24} />
                    <span>Ver Video</span>
                  </a>
                </div>
              )}

              {/* Document Content - Markdown or PDF */}
              {document.file_type === 'markdown' ? (
                <div className={`markdown-content ${isDarkMode ? 'markdown-dark' : 'markdown-light'}`}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {document.content}
                  </ReactMarkdown>
                </div>
              ) : (
                <PDFViewer 
                  pdfBase64={document.content} 
                  isDarkMode={isDarkMode}
                  documentTitle={document.title}
                />
              )}

              {/* Document Metadata */}
              <div className={`mt-8 pt-6 border-t ${
                isDarkMode ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Categoría:{' '}
                    </span>
                    <span className={`font-semibold ${
                      isDarkMode ? 'text-purple-400' : 'text-purple-600'
                    }`}>
                      {document.category}
                    </span>
                  </div>
                  {document.updated_at && (
                    <div>
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                        Actualizado:{' '}
                      </span>
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                        {new Date(document.updated_at).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Markdown Styles */}
      <style jsx>{`
        .markdown-content h1 {
          font-size: 2em;
          font-weight: bold;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
        }
        .markdown-content h2 {
          font-size: 1.5em;
          font-weight: 600;
          margin-top: 1.25rem;
          margin-bottom: 0.75rem;
        }
        .markdown-content h3 {
          font-size: 1.25em;
          font-weight: 600;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
        }
        .markdown-content p {
          margin-bottom: 1rem;
          line-height: 1.7;
        }
        .markdown-content ul,
        .markdown-content ol {
          margin-bottom: 1rem;
          margin-left: 1.5rem;
        }
        .markdown-content li {
          margin-bottom: 0.5rem;
        }
        .markdown-content code {
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-family: 'Courier New', monospace;
        }
        .markdown-content pre {
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin-bottom: 1rem;
        }
        .markdown-content blockquote {
          border-left: 4px solid;
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
        }
        .markdown-content a {
          text-decoration: underline;
        }
        .markdown-content strong {
          font-weight: bold;
        }
        .markdown-content em {
          font-style: italic;
        }
        
        /* Light mode styles */
        .markdown-light code {
          background-color: #f3f4f6;
          color: #1f2937;
        }
        .markdown-light pre {
          background-color: #f9fafb;
        }
        .markdown-light pre code {
          background-color: transparent;
        }
        .markdown-light blockquote {
          border-color: #9333ea;
          color: #6b7280;
        }
        .markdown-light a {
          color: #9333ea;
        }
        .markdown-light a:hover {
          color: #7e22ce;
        }
        
        /* Dark mode styles */
        .markdown-dark code {
          background-color: #374151;
          color: #e5e7eb;
        }
        .markdown-dark pre {
          background-color: #1f2937;
        }
        .markdown-dark pre code {
          background-color: transparent;
        }
        .markdown-dark blockquote {
          border-color: #a78bfa;
          color: #d1d5db;
        }
        .markdown-dark a {
          color: #a78bfa;
        }
        .markdown-dark a:hover {
          color: #c4b5fd;
        }
      `}</style>
    </div>
  );
};

export default DocumentoViewPage;
